import type { ReactNode } from 'react'

import { Box, CircularProgress, Stack, Typography, alpha, useTheme } from '@mui/material'
import {
  CheckCircle2Icon,
  CircleXIcon,
  InfoIcon,
  RadioIcon,
  RadioReceiverIcon,
  TriangleAlertIcon,
} from 'lucide-react'

import { Alert, Button, Switch } from '@repo/ui'
import { StatusChip, familyLockMode } from 'components'
import type { TagFamily } from 'components'
import type { UseEncodeSessionReturn } from 'hooks'
import { PACKAGE_FILL_STATUSES } from 'constants/statuses'

/**
 * VRG-252 — "Lock" is ONE toggle and TWO unrelated mechanisms, so the warning has to say which one
 * the operator is agreeing to. Both are irreversible; what they protect, and what happens if the
 * encode was wrong, differ:
 *
 * - `sdm_key_rotation` (424): the chip's SDM keys are rotated to per-tag Vera keys. The chip stays
 *   writable to whoever holds those keys, and Vera stores them — so the tag remains manageable.
 * - `permanent_static_lock` (Connect / NTAG 213, EM near-field): the lock BITS are set and the chip
 *   becomes physically read-only. There is no key and no way back for anyone, including Vera.
 *
 * The Connect copy also has to be honest about the CEILING, because the toggle otherwise reads as
 * "this makes the tag secure": locking stops anyone rewriting THIS chip, and does not stop the URL
 * being copied onto another one. That is the difference between tamper-resistant and
 * tamper-evident, and it is the whole reason Connect is a different method rather than a weak 424.
 */
const LOCK_COPY: Record<string, {label: string; title: string; description: string}> = {
  sdm_key_rotation: {
    label: 'Production keys',
    title: 'Locking is permanent',
    description:
      "Each tag's keys are rotated to Vera keys and can't be reset. Only use a sacrificial tag while testing.",
  },
  permanent_static_lock: {
    label: 'Permanently read-only',
    title: 'Locking is permanent',
    description:
      "Each tag is made physically read-only and can never be rewritten, by anyone. This stops the tag being altered. It does not stop its address being copied onto a different tag, so these tags connect to the certificate rather than proving the item. Only use a sacrificial tag while testing.",
  },
}

interface EncodeSessionBodyProps {
  session: UseEncodeSessionReturn
  // Reader connection (from useEncodingDevice in the container).
  helperReachable: boolean
  isConnected: boolean
  readerName: string | null
  // Lock is chosen BEFORE the session starts and fixed for its duration (default OFF = testing).
  lock: boolean
  setLock: (v: boolean) => void
  // Big counter: the package's true total (server-sourced) vs its target capacity.
  bigCount: number
  targetCount?: number
  isPackageFilled: boolean
  // "in this package" (package session) | "tags encoded" (company-direct test session).
  counterLabel: string
  // Bulk-only: a progress/tally line rendered between the reader row and the counter.
  header?: ReactNode
  // Bulk-only: false while awaiting a box (scanning / loading the queue) — renders `placeholder`
  // instead of the counter + running list. Defaults to true (single flow is always active).
  active?: boolean
  placeholder?: ReactNode
  // VERA-790 confirm-to-move: uids already in another package/company than the pending assign target
  // — these rows get a "Will move here" caption so it's clear which tags the move warning refers to.
  conflictUids?: Set<string>
  // VRG-252 — the session's chip family, so the lock toggle can describe the mechanism it actually
  // triggers. Undefined falls back to the 424 wording, which is the historical behaviour.
  family?: TagFamily | null
}

/**
 * VERA-790 — the shared VISUAL body of an encode session: reader-status row, the big counter +
 * lock toggle + waiting/alert, and the scrollable running list. Rendered inside BOTH the single
 * `EncodeTagsModal` and the bulk `BulkEncodeTagsModal`, so the session surface stays identical
 * across the single and assembly-line flows. All session state comes from `useEncodeSession`; the
 * container owns lock state, the counter math, and (for bulk) the per-box orchestration around it.
 */
export const EncodeSessionBody = (props: EncodeSessionBodyProps) => {
  const { session, helperReachable, isConnected, readerName, lock, setLock } = props
  const { bigCount, targetCount, isPackageFilled, counterLabel } = props
  const { header, active = true, placeholder, conflictUids, family } = props
  const lockCopy = LOCK_COPY[familyLockMode(family) ?? 'sdm_key_rotation'] ?? LOCK_COPY.sdm_key_rotation
  const theme = useTheme()

  const {
    visibleRows,
    provisioned,
    quarantined,
    provisioning,
    alreadyInPackageCount,
    sessionActive,
    waiting,
    alert,
    showQuarantinedOnly,
    setShowQuarantinedOnly,
    retryAsReassign,
  } = session

  const readerReady = helperReachable && isConnected

  return (
    <Stack spacing={4}>
      {/* Reader status — parity with the Create Certificates modal's connected-reader row: the
          label/status sits left, and a "Connected" brand pill + reader glyph sits rightmost. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={3}
        sx={{
          border: `1px solid ${theme.palette.primary[100]}`,
          borderRadius: 2,
          px: 4,
          py: 3,
        }}
      >
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2">
            {readerReady
              ? readerName || 'Vera Desk Reader'
              : helperReachable
                ? 'No reader detected'
                : 'Reader helper not running'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {readerReady
              ? 'Ready to encode. Present one tag at a time.'
              : 'Start the Vera Reader Helper and plug in the reader to begin.'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
          {readerReady && (
            <Typography variant="body3" color={theme.palette.brand[500]}>
              Connected
            </Typography>
          )}
          <RadioReceiverIcon
            strokeWidth={1}
            color={readerReady ? theme.palette.brand[500] : theme.palette.text.disabled}
          />
        </Stack>
      </Stack>

      {/* Bulk-only progress/tally line, between the reader row and the counter. */}
      {header}

      {/* While awaiting a box (bulk scan/queue), show the placeholder instead of the counter. */}
      {!active ? (
        placeholder
      ) : (
        <>
      {/* Counter + lock toggle. */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.primary[100]}`,
          borderRadius: 2,
          px: 4,
          py: 4,
        }}
      >
        <Stack spacing={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={2} alignItems="baseline">
                <Typography variant="h4">{bigCount}</Typography>
                {targetCount != null && (
                  <Typography variant="body3" color="text.secondary">
                    / {targetCount}
                  </Typography>
                )}
                {isPackageFilled && (
                  <StatusChip size="small" statuses={PACKAGE_FILL_STATUSES} status="filled" />
                )}
              </Stack>
              <Typography variant="body3" color="text.secondary">
                {counterLabel}
                {provisioning > 0 ? ` · ${provisioning} saving` : ''}
                {quarantined > 0 ? ` · ${quarantined} quarantined` : ''}
                {alreadyInPackageCount > 0 ? ` · ${alreadyInPackageCount} already in package` : ''}
              </Typography>
            </Stack>
            {/* Lock toggle — fixed for the session; default OFF (testing / all-zero keys). */}
            <Stack direction="row" spacing={3} alignItems="center">
              <Stack spacing={0} alignItems="flex-end">
                <Typography variant="body3">Lock tags</Typography>
                <Typography variant="caption" color="text.secondary">
                  {lock ? lockCopy.label : 'Off (testing)'}
                </Typography>
              </Stack>
              <Switch
                checked={lock}
                disabled={sessionActive}
                onChange={(_e: unknown, checked: boolean) => setLock(checked)}
              />
            </Stack>
          </Stack>

          {/* Lock safety guard — PERMANENT either way, but by a different mechanism per family. */}
          {lock && (
            <Alert severity="warning" title={lockCopy.title} description={lockCopy.description} />
          )}

          {waiting && (
            <Stack direction="row" spacing={2} alignItems="center">
              <RadioIcon strokeWidth={1.5} color={theme.palette.primary.main} />
              <Typography variant="body3" color="text.secondary">
                Present a tag on the reader…
              </Typography>
            </Stack>
          )}

          {alert && <Alert severity={alert.severity} description={alert.message} />}
        </Stack>
      </Box>

      {/* Running list — scrollable, newest on top. Capped so a full box stays a bounded panel. */}
      {visibleRows.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${theme.palette.primary[100]}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 4, py: 2, borderBottom: `1px solid ${theme.palette.primary[100]}` }}
          >
            <Typography variant="body3" color="text.secondary">
              {provisioned} encoded
              {provisioning > 0 ? ` · ${provisioning} saving` : ''}
              {quarantined > 0 ? ` · ${quarantined} quarantined` : ''}
              {alreadyInPackageCount > 0 ? ` · ${alreadyInPackageCount} already in package` : ''}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {quarantined > 0 && (
                <Typography
                  variant="caption"
                  onClick={() => setShowQuarantinedOnly((v) => !v)}
                  sx={{
                    cursor: 'pointer',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: showQuarantinedOnly
                      ? alpha(theme.palette.error.main, 0.12)
                      : 'transparent',
                    color: showQuarantinedOnly ? theme.palette.error.main : theme.palette.text.secondary,
                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) },
                  }}
                >
                  {showQuarantinedOnly ? 'Show all' : `Show ${quarantined} quarantined`}
                </Typography>
              )}
              {/* Bulk "Add all here" — reassign EVERY already-in-another-package row into this box at
                  once. Only when there's MORE THAN ONE (a single row has its own inline button), and
                  to the RIGHT of the quarantine text. */}
              {session.encoded.filter((t) => t.kind === 'conflict').length > 1 && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    session.encoded
                      .filter((t) => t.kind === 'conflict')
                      .forEach((r) => retryAsReassign(r))
                  }
                >
                  Add all here
                </Button>
              )}
            </Stack>
          </Stack>
          <Stack
            divider={<div style={{ borderTop: `1px solid ${theme.palette.primary[100]}` }} />}
            sx={{ maxHeight: 320, overflowY: 'auto' }}
          >
            {visibleRows.map((t, i) => (
              <Stack
                key={`${t.uid}-${i}`}
                direction="row"
                spacing={3}
                alignItems="center"
                sx={{ px: 4, py: 2.5 }}
              >
                {t.status === 'provisioned' ? (
                  <CheckCircle2Icon strokeWidth={1.5} color={theme.palette.success.main} />
                ) : t.status === 'provisioning' ? (
                  <CircularProgress size={20} thickness={4} sx={{ color: theme.palette.text.disabled }} />
                ) : t.status === 'already' ? (
                  <InfoIcon strokeWidth={1.5} color={theme.palette.text.disabled} />
                ) : t.kind === 'conflict' || t.kind === 'full' ? (
                  // Already in ANOTHER package (recoverable via "Add here instead"), or the box is
                  // full — both are amber, not a hard red error.
                  <TriangleAlertIcon strokeWidth={1.5} color={theme.palette.warning.main} />
                ) : (
                  <CircleXIcon strokeWidth={1.5} color={theme.palette.error.main} />
                )}
                <Stack spacing={0} sx={{ flex: 1 }}>
                  <Typography variant="body3" sx={{ fontFamily: 'monospace' }}>
                    {t.uid}
                  </Typography>
                  {t.status === 'quarantined' && t.reason && (
                    <Typography
                      variant="caption"
                      color={t.kind === 'conflict' || t.kind === 'full' ? 'warning.main' : 'error.main'}
                    >
                      {t.reason}
                    </Typography>
                  )}
                  {t.status === 'already' && t.reason && (
                    <Typography variant="caption" color="text.secondary">
                      {t.reason}
                    </Typography>
                  )}
                  {conflictUids?.has(t.uid) && (
                    <Typography variant="caption" color="warning.main">
                      Will move here
                    </Typography>
                  )}
                </Stack>
                {t.kind === 'conflict' && t.conflictJob && (
                  <Button variant="secondary" onClick={() => retryAsReassign(t)}>
                    Add here instead
                  </Button>
                )}
                {t.locked && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: 'text.secondary',
                    }}
                  >
                    locked
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
        </>
      )}
    </Stack>
  )
}
